import { AuthContextValue } from "../context/AuthContext.tsx";
import {
  CashAccounts,
  IncomeEvent,
  OwnerPayConfig,
  RecurringIncome,
} from "../types.ts";
import {
  createIncomeEvent,
  createRecurringIncome,
  deleteIncomeEvent,
  deleteRecurringIncome,
  getCashAccounts,
  getIncomeEvents,
  getOwnerPayConfig,
  getRecurringIncome,
  isCashflowApiEnabled,
  updateCashAccounts,
  updateIncomeEvent,
  updateOwnerPayConfig,
  updateRecurringIncome,
} from "../requests/cashflow.ts";
import { trackError } from "./mixpanel.ts";

/**
 * Single swap point between "localStorage only" and "backed by the cash-flow
 * API". While REACT_APP_CASHFLOW_API is unset the atoms self-hydrate from
 * localStorage and these helpers are inert; flip the flag once the endpoints in
 * docs/CASHFLOW_API.md exist and the same atoms get seeded from / written to the
 * server.
 */

interface CashflowSetters {
  setAccounts: (val: CashAccounts) => void;
  setIncomeEvents: (val: IncomeEvent[]) => void;
  setOwnerPayConfig: (val: OwnerPayConfig) => void;
  setRecurringIncome: (val: RecurringIncome[]) => void;
}

export const hydrateCashflow = async (
  auth: AuthContextValue,
  setters: CashflowSetters,
) => {
  if (!isCashflowApiEnabled()) {
    return;
  }

  try {
    const accessToken = await auth.getAccessTokenSilently();

    const [accounts, events, config, recurring] = await Promise.all([
      getCashAccounts(accessToken),
      getIncomeEvents(accessToken),
      getOwnerPayConfig(accessToken),
      getRecurringIncome(accessToken),
    ]);

    accounts && setters.setAccounts(accounts);
    Array.isArray(events) && setters.setIncomeEvents(events);
    config && setters.setOwnerPayConfig(config);
    Array.isArray(recurring) && setters.setRecurringIncome(recurring);
  } catch (err) {
    trackError("cashflowSync - hydrateCashflow:", { result: err });
  }
};

export const persistCashAccounts = async (
  auth: AuthContextValue,
  accounts: CashAccounts,
) => {
  if (!isCashflowApiEnabled()) {
    return;
  }

  try {
    const accessToken = await auth.getAccessTokenSilently();

    await updateCashAccounts(accessToken, accounts);
  } catch (err) {
    trackError("cashflowSync - persistCashAccounts:", { result: err });
  }
};

export const persistIncomeEvent = async (
  auth: AuthContextValue,
  op: "create" | "update" | "delete",
  event: IncomeEvent,
) => {
  if (!isCashflowApiEnabled()) {
    return;
  }

  try {
    const accessToken = await auth.getAccessTokenSilently();

    if (op === "create") {
      await createIncomeEvent(accessToken, event);
    } else if (op === "update") {
      await updateIncomeEvent(accessToken, event);
    } else {
      await deleteIncomeEvent(accessToken, event.id);
    }
  } catch (err) {
    trackError("cashflowSync - persistIncomeEvent:", { result: err, op });
  }
};

export const persistRecurringIncome = async (
  auth: AuthContextValue,
  op: "create" | "update" | "delete",
  rule: RecurringIncome,
) => {
  if (!isCashflowApiEnabled()) {
    return;
  }

  try {
    const accessToken = await auth.getAccessTokenSilently();

    if (op === "create") {
      await createRecurringIncome(accessToken, rule);
    } else if (op === "update") {
      await updateRecurringIncome(accessToken, rule);
    } else {
      await deleteRecurringIncome(accessToken, rule.id);
    }
  } catch (err) {
    trackError("cashflowSync - persistRecurringIncome:", { result: err, op });
  }
};

export const persistOwnerPayConfig = async (
  auth: AuthContextValue,
  config: OwnerPayConfig,
) => {
  if (!isCashflowApiEnabled()) {
    return;
  }

  try {
    const accessToken = await auth.getAccessTokenSilently();

    await updateOwnerPayConfig(accessToken, config);
  } catch (err) {
    trackError("cashflowSync - persistOwnerPayConfig:", { result: err });
  }
};
